import { Formik, Form, Field, ErrorMessage, type FormikHelpers } from "formik"
import * as Yup from "yup"
import styled from "styled-components"
import api from "@utils/api.client"
import { trackEvent } from "@components/utils/tracking.client"
import { useState } from "react"
import { SuccessBanner } from "@components/base/Banner/SuccessBanner"

const FormWrapper = styled.section`
  position: relative;
  background: #ffffff;
  padding: clamp(2rem, 6vw, 4.5rem);
  border: 1px solid #b9c9d6;
  border-top: 4px solid #1b75bc;
  border-radius: 2px;
  box-shadow: 0 24px 60px rgba(18, 55, 88, 0.08);
  display: grid;
  gap: 1.5rem;
  min-width: 0;

  > form {
    width: 100%;
    min-width: 0;
  }
`

const FormTitle = styled.h3`
  max-width: 780px;
  font-size: clamp(2rem, 4vw, 3.6rem);
  line-height: 1;
  letter-spacing: -0.045em;
  color: #102f4f;
  margin: 0;
`

const FormSubtitle = styled.p`
  margin: 0;
  max-width: 800px;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #52687c;
`

const Grid = styled.div`
  display: grid;
  gap: 1.25rem 1.75rem;
  grid-template-columns: 1fr;
  min-width: 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const FieldWrapper = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #173a5d;
  min-width: 0;
`

const Input = styled(Field)`
  width: 100%;
  min-width: 0;
  min-height: 52px;
  border: 1px solid #b9c9d6;
  border-radius: 3px;
  padding: 0.8rem 0.9rem;
  font-size: 1rem;
  font-weight: 400;
  background-color: #fbfdff;
  color: #172c42;

  &:focus {
    outline: 3px solid rgba(51, 122, 189, 0.14);
    border-color: #337abd;
  }
`

const TextArea = styled(Field)`
  width: 100%;
  min-width: 0;
  border: 1px solid #b9c9d6;
  border-radius: 3px;
  padding: 0.85rem 0.9rem;
  font-size: 1rem;
  font-weight: 400;
  min-height: 130px;
  resize: vertical;
  background-color: #fbfdff;
  color: #172c42;

  &:focus {
    outline: 3px solid rgba(51, 122, 189, 0.14);
    border-color: #337abd;
  }
`

const Select = styled(Field)`
  width: 100%;
  min-width: 0;
  min-height: 52px;
  border: 1px solid #b9c9d6;
  border-radius: 3px;
  padding: 0.8rem 0.85rem;
  font-size: 1rem;
  font-weight: 400;
  background-color: #fbfdff;
  color: #172c42;

  &:focus {
    outline: 3px solid rgba(51, 122, 189, 0.14);
    border-color: #337abd;
  }
`

const ErrorText = styled.div`
  font-size: 0.8rem;
  color: #d93025;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  margin-top: 1rem;
`

const SubmitButton = styled.button`
  min-height: 54px;
  background: #175f98;
  color: #ffffff;
  border: 1px solid #175f98;
  border-radius: 3px;
  padding: 0 1.6rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: #0d4172;
    border-color: #0d4172;
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(27, 117, 188, 0.2);
  }
`

const SuccessActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin: 0.75rem 0 1.25rem;
`

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.25rem;
  border-radius: 3px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`

const PrimaryActionButton = styled(ActionButton)`
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);

  &:hover {
    background: ${({ theme }) => theme.primaryDark};
  }
`

const SecondaryActionButton = styled(ActionButton)`
  color: ${({ theme }) => theme.primary};
  border: 1.5px solid ${({ theme }) => theme.primary};
  background: #fff;

  &:hover {
    background: rgba(27, 117, 188, 0.04);
  }
`

const LegalNote = styled.small`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary || theme.textPrimary};
`

type FieldType = "text" | "textarea" | "select"

export interface AdditionalFieldConfig {
  name: string
  label: string
  type: FieldType
  placeholder?: string
  options?: { label: string; value: string }[]
  fullWidth?: boolean
}

interface ConsultationFormProps {
  inquiryType: string
  title: string
  description: string
  additionalFields?: AdditionalFieldConfig[]
}

interface FormValues {
  firstName: string
  lastName: string
  email: string
  phone: string
  legalEntity: string
  numBuildings: string
  numUnits: string
  sector: string
  message: string
  [key: string]: string
}

const validationSchema = Yup.object({
  firstName: Yup.string().required("Prénom requis"),
  lastName: Yup.string().required("Nom requis"),
  email: Yup.string().email("Courriel invalide").required("Courriel requis"),
  phone: Yup.string()
    .matches(/^\D*(\d\D*){10}$/, "Entrez un numéro de téléphone à 10 chiffres")
    .required("Téléphone requis"),
  numBuildings: Yup.number().typeError("Nombre invalide").min(1, "Minimum 1").required("Obligatoire"),
  numUnits: Yup.number().typeError("Nombre invalide").min(1, "Minimum 1").required("Obligatoire"),
  sector: Yup.string().required("Ville ou secteur requis"),
})

const ConsultationForm = ({ inquiryType, title, description, additionalFields = [] }: ConsultationFormProps) => {
  const [success, setSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const initialValues = additionalFields.reduce<FormValues>(
    (values, field) => {
      return { ...values, [field.name]: "" }
    },
    {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      legalEntity: "",
      numBuildings: "1",
      numUnits: "4",
      sector: "",
      message: "",
    },
  )

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setErrorMessage(null)
    setSuccess(false)
    try {
      const { firstName, lastName, email, phone, legalEntity, numBuildings, numUnits, sector, message, ...extras } = values

      const formattedExtras = additionalFields
        .map((field) => [field.label, extras[field.name]] as const)
        .filter(([, value]) => Boolean(value))
        .map(([label, value]) => `${label}: ${value}`)

      const payload = {
        inquiryType,
        firstName,
        lastName,
        email,
        phone: phone.replace(/\D/g, ""),
        legalEntity,
        numBuildings: Number(numBuildings),
        numUnits: Number(numUnits),
        sector,
        additionalInfo: [message, ...formattedExtras].filter(Boolean).join("\n"),
      }

      await api.post("/request-contact-form", payload)
      trackEvent("generate_lead", { form_name: "consultation_service", service: inquiryType })
      setSuccess(true)
      resetForm()
    } catch {
      setErrorMessage("Une erreur est survenue. Veuillez réessayer ou nous appeler au (418) 684-9004.")
    }
  }

  return (
    <FormWrapper>
      {success && (
        <div>
          <SuccessBanner>Merci! Votre demande a été transmise à notre équipe.</SuccessBanner>
          <SuccessActions>
            <PrimaryActionButton href="/">Retour à l'accueil</PrimaryActionButton>
            <SecondaryActionButton href="/guide-proprietaires-portneuf">Explorer nos ressources propriétaires</SecondaryActionButton>
          </SuccessActions>
        </div>
      )}
      <FormTitle>{title}</FormTitle>
      <FormSubtitle>{description}</FormSubtitle>
      <p style={{ fontSize: "0.9rem", color: "#6b7280", marginTop: "0rem" }}>Les demandes sont traitées pendant nos heures d’ouverture.</p>
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <Form noValidate>
            <Grid>
              <FieldWrapper>
                Prénom
                <Input name="firstName" type="text" autoComplete="given-name" />
                <ErrorMessage component={ErrorText} name="firstName" />
              </FieldWrapper>
              <FieldWrapper>
                Nom
                <Input name="lastName" type="text" autoComplete="family-name" />
                <ErrorMessage component={ErrorText} name="lastName" />
              </FieldWrapper>
              <FieldWrapper>
                Courriel
                <Input name="email" type="email" autoComplete="email" />
                <ErrorMessage component={ErrorText} name="email" />
              </FieldWrapper>
              <FieldWrapper>
                Téléphone
                <Input name="phone" type="tel" autoComplete="tel" />
                <ErrorMessage component={ErrorText} name="phone" />
              </FieldWrapper>
              <FieldWrapper>
                Nombre d'immeubles à gérer
                <Input name="numBuildings" type="number" min="1" />
                <ErrorMessage component={ErrorText} name="numBuildings" />
              </FieldWrapper>
              <FieldWrapper>
                Nombre total d'unités
                <Input name="numUnits" type="number" min="1" />
                <ErrorMessage component={ErrorText} name="numUnits" />
              </FieldWrapper>
              <FieldWrapper>
                Ville ou secteur principal
                <Input name="sector" type="text" placeholder="Neuville, Donnacona, Cap-Santé..." />
                <ErrorMessage component={ErrorText} name="sector" />
              </FieldWrapper>
              <FieldWrapper>
                Entité légale (optionnel)
                <Input name="legalEntity" type="text" placeholder="Société, fiducie, etc." />
              </FieldWrapper>
              {additionalFields.map((field) => (
                <FieldWrapper key={field.name} style={{ gridColumn: field.fullWidth ? "1 / -1" : undefined }}>
                  {field.label}
                  {field.type === "select" ? (
                    <Select name={field.name} as="select">
                      <option value="">Sélectionner</option>
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : field.type === "textarea" ? (
                    <TextArea name={field.name} as="textarea" placeholder={field.placeholder} />
                  ) : (
                    <Input name={field.name} type="text" placeholder={field.placeholder} />
                  )}
                </FieldWrapper>
              ))}
              <FieldWrapper style={{ gridColumn: "1 / -1" }}>
                Détails supplémentaires
                <TextArea name="message" as="textarea" placeholder="Partagez vos objectifs, contraintes budgétaires ou besoins particuliers." />
              </FieldWrapper>
            </Grid>
            <Actions>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Envoi..." : "Soumettre ma demande"}
              </SubmitButton>
              <LegalNote>
                En soumettant ce formulaire, vous acceptez que TB Gestion Immobilière communique avec vous pour valider votre projet et planifier une
                consultation.
              </LegalNote>
            </Actions>
          </Form>
        )}
      </Formik>
    </FormWrapper>
  )
}

export default ConsultationForm
